import { computed, ref } from 'vue'
import type VueRouter from 'vue-router'

import util from '@/client/core/util'

import { useTwBenchmarkPostsStore } from '../store/useTwBenchmarkPostsStore'

const BASE_PATH = '/twitter_benchmark_analytics'

export interface UsePostDetailDrawerOptions {
  router?: VueRouter
  loadPage?: (page: number) => Promise<void>
}

/**
 * 詳細ドロワー用パス（例: /twitter_benchmark_analytics/123）を組み立てる純関数。
 */
export function buildDetailPath(postId: number): string {
  return `${BASE_PATH}/${postId}`
}

/**
 * 投稿詳細ドロワー。開閉・前後ナビに URL を追従させて直接アクセス時の復元を可能にし、前後ナビはページを跨いで全件を横断する。
 */
export function usePostDetailDrawer(options: UsePostDetailDrawerOptions = {}) {
  const { router, loadPage } = options
  const postsStore = useTwBenchmarkPostsStore()

  const isOpen = ref(false)
  const currentPostId = ref<number | null>(null)

  const currentIndex = computed(() => {
    if (currentPostId.value === null) return -1
    return postsStore.posts.findIndex(item => item.post.id === currentPostId.value)
  })

  const globalIndex = computed(() => {
    if (currentIndex.value < 0) return -1
    return (postsStore.currentPage - 1) * postsStore.pageSize + currentIndex.value
  })

  const isPrev = computed(() => globalIndex.value > 0)
  const isNext = computed(() => globalIndex.value >= 0 && globalIndex.value < postsStore.totalCount - 1)

  const total = computed(() => postsStore.totalCount)

  let savedListQuery: Record<string, string | (string | null)[]> = {}

  const syncUrlToPost = (postId: number): void => {
    if (router === undefined) return
    const target = buildDetailPath(postId)
    if (router.currentRoute.path === target) return
    if (router.currentRoute.path === BASE_PATH) {
      savedListQuery = { ...(router.currentRoute.query as Record<string, string | (string | null)[]>) }
    }
    void router.push({ path: target, query: {} }).catch(() => {
      // 同一ルートへの push は NavigationDuplicated で reject されるため無視する
    })
  }

  const syncSavedListPage = (): void => {
    const rest = { ...savedListQuery }
    delete rest.page
    savedListQuery = postsStore.currentPage === 1 ? rest : { ...rest, page: String(postsStore.currentPage) }
  }

  const syncUrlToList = (): void => {
    if (router === undefined) return
    if (router.currentRoute.path === BASE_PATH) return
    void router.push({ path: BASE_PATH, query: savedListQuery }).catch(() => {
      // 同一ルートへの push は NavigationDuplicated で reject されるため無視する
    })
  }

  const loadDetail = async (postId: number): Promise<void> => {
    isOpen.value = true
    currentPostId.value = postId

    await postsStore.fetchPostDetail(postId)

    if (postsStore.detail === null) {
      isOpen.value = false
      currentPostId.value = null
    }
  }

  const open = async (postId: number): Promise<void> => {
    syncUrlToPost(postId)
    await loadDetail(postId)
    if (!isOpen.value) {
      syncUrlToList()
    }
  }

  const close = (): void => {
    isOpen.value = false
    currentPostId.value = null
    syncUrlToList()
  }

  const navigatePrev = async (): Promise<void> => {
    if (!isPrev.value) return

    if (currentIndex.value > 0) {
      const prev = postsStore.posts[currentIndex.value - 1]
      await open(prev.post.id)
      return
    }

    if (loadPage === undefined || postsStore.currentPage <= 1) return
    await loadPage(postsStore.currentPage - 1)
    syncSavedListPage()
    util.scrollToTop()
    const last = postsStore.posts[postsStore.posts.length - 1]
    if (last) await open(last.post.id)
  }

  const navigateNext = async (): Promise<void> => {
    if (!isNext.value) return

    if (currentIndex.value >= 0 && currentIndex.value < postsStore.posts.length - 1) {
      const next = postsStore.posts[currentIndex.value + 1]
      await open(next.post.id)
      return
    }

    if (loadPage === undefined) return
    await loadPage(postsStore.currentPage + 1)
    syncSavedListPage()
    util.scrollToTop()
    const first = postsStore.posts[0]
    if (first) await open(first.post.id)
  }

  const syncFromRoute = async (postId: number | null): Promise<void> => {
    if (postId === null) {
      isOpen.value = false
      currentPostId.value = null
      return
    }
    if (isOpen.value && currentPostId.value === postId) return
    await loadDetail(postId)
  }

  return {
    isOpen,
    currentPostId,
    currentIndex,
    globalIndex,
    total,
    isPrev,
    isNext,
    open,
    close,
    navigatePrev,
    navigateNext,
    syncFromRoute
  }
}
